import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  ablehnen,
  arbeitsliste as ladeArbeitsliste,
  eine as ladeEine,
  verlaengern,
  vorschlagen,
  zusagen,
  type Optionsalternative,
  type Optionsanfrage,
  type Optionsarbeitszeile,
  type Optionswunschtag,
} from '../api/optionsanfragen';
import { kalender, type Kalendertermin } from '../api/termine';
import { Dialog } from '../komponenten/Dialog';
import { Maskenverweis, Tagesverweis } from '../komponenten/Maskenverweis';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Die Arbeitsliste der Disposition (ANG-38).
 *
 * ============================================================================
 * DER SATZ, DER DIESE MASKE BESTIMMT
 * ============================================================================
 *
 * ANG-38: „Arbeitsliste der Disposition mit allen offenen und eskalierten
 * Optionsanfragen: Kunde, Wunschtermine, Teilnehmerzahl, anfragender Benutzer,
 * Eingang und Restfrist. KEIN VORGANG VERSCHWINDET DARAUS, BEVOR ER
 * BEANTWORTET IST."
 *
 * Der letzte Satz ist der Grund für die Maske und zugleich ihre Bauvorschrift:
 *
 *   KEIN FILTER, DER ETWAS WEGNIMMT. Eine Liste, die man erst einstellen muss,
 *   verbirgt genau das, was sie zeigen soll. Sortiert wird — nach Dringlichkeit
 *   —, aber nichts fällt heraus.
 *
 *   HALB BEANTWORTET IST NICHT BEANTWORTET. Eine Anfrage mit drei Alternativen
 *   bleibt stehen, bis alle drei eine Antwort tragen. Das rechnet der Server
 *   (`erledigtAm` folgt aus den Alternativen) und nicht diese Maske.
 *
 * ============================================================================
 * WAS OHNE TABELLE 29 GESCHIEHT
 * ============================================================================
 *
 * Die Vorgabewerte für Antwort-, Options- und Vorlauffrist stehen in Tabelle 29
 * des Lastenhefts; sie liegt dem Team nicht vor, und die drei Einstellungen
 * sind leer. Die Maske sagt das an der Stelle, an der es auffällt: Eine
 * Anfrage ohne Antwortfrist zeigt „keine Frist" statt einer erfundenen Zahl,
 * und die Zusage verlangt dann ein Datum.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Der Zähler steht im Seitenkopf, der Streifen „ob etwas brennt" als Hinweis
 * über der Tabelle, die Tabelle in der Karte. Eine Filterleiste gibt es nicht
 * — aus dem Grund oben: kein Filter, der etwas wegnimmt.
 */

/**
 * Ein Wunschtermin mit seiner Uhrzeit — „04.12.2026, 08:00 Uhr".
 *
 * Die Arbeitsliste liefert je Alternative `2026-12-04T08:00` oder, wo keine
 * Uhrzeit gewünscht ist (ANG-25, der Wunsch über einen ZEITRAUM), `2026-12-04`.
 * Eine erfundene Null-Uhr wäre eine Angabe, die niemand gemacht hat.
 *
 * ZERLEGT WIRD DER TEXT, NICHT EIN `Date`. Der Wunsch steht in ORTSZEIT — so
 * hat ihn der Kunde genannt und so steht er in der Datenbank (`date` und
 * `time`, nicht `timestamptz`). Durch `new Date()` geschickt würde er als
 * Zeitpunkt der Prozesszone gelesen und in Europe/Berlin um eine Stunde
 * verschoben: Aus 08:00 wurde 09:00. Ein Kalendertag ist keine Zeitangabe
 * (FRS-01, LOG-03) — und eine Wunschuhrzeit auch nicht.
 */
function alsWunsch(iso: string): string {
  const [tag = '', zeit] = iso.split('T');
  const gesetzt = alsTag(tag);
  return zeit === undefined || zeit === '' ? gesetzt : `${gesetzt}, ${zeit.slice(0, 5)} Uhr`;
}

function alsTag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

function alsZeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Die Restfrist in Worten (ANG-38).
 *
 * IN STUNDEN UND TAGEN, NICHT IN MINUTEN: „noch 1 Tag 4 Std." ist eine
 * Auskunft, „noch 1687 Minuten" eine Rechenaufgabe. Unter einer Stunde zählt
 * die Minute wieder, weil sie dann trägt.
 */
export function restfrist(minuten: number | null): string {
  if (minuten === null) return 'keine Frist';
  if (minuten < 0) {
    const ueber = Math.abs(minuten);
    if (ueber < 1440) return `seit ${String(Math.floor(ueber / 60))} Std. überfällig`;
    const tage = Math.floor(ueber / 1440);
    return `seit ${tage === 1 ? 'einem Tag' : `${String(tage)} Tagen`} überfällig`;
  }
  if (minuten < 60) return `noch ${String(minuten)} Min.`;
  if (minuten < 1440) return `noch ${String(Math.floor(minuten / 60))} Std.`;
  const tage = Math.floor(minuten / 1440);
  const stunden = Math.floor((minuten % 1440) / 60);
  /* „1 Tage" ist ein Fehler, den jeder sieht und der die Maske billig aussehen laesst. */
  const tagwort = tage === 1 ? '1 Tag' : `${String(tage)} Tage`;
  return stunden === 0 ? `noch ${tagwort}` : `noch ${tagwort} ${String(stunden)} Std.`;
}

/** Der Tag im Format, das `<input type="date">` versteht. */
function alsFeldtag(iso: string): string {
  return iso.slice(0, 10);
}

function heutePlus(tage: number): string {
  const d = new Date();
  d.setDate(d.getDate() + tage);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Eine Alternative mit ihren drei Antworten (ANG-32).
 *
 * „Die Disposition beantwortet jede Alternative EINZELN: zusagen, ablehnen oder
 * einen abweichenden Termin vorschlagen." Deshalb drei Wege nebeneinander und
 * kein Auswahlfeld mit drei Werten: Jeder trägt andere Angaben, und ein Feld,
 * das je nach Auswahl etwas anderes bedeutet, wird falsch ausgefüllt.
 */
export function Alternativzeile({
  anfrage,
  alternative,
  beiAntwort,
}: {
  readonly anfrage: Optionsanfrage;
  readonly alternative: Optionsalternative;
  readonly beiAntwort: () => Promise<void>;
}): JSX.Element {
  const [termine, setzeTermine] = useState<readonly Kalendertermin[]>([]);
  const [termin, setzeTermin] = useState('');
  const [frist, setzeFrist] = useState(anfrage.gewuenschteFristBis ?? '');
  const [grund, setzeGrund] = useState('');
  const [vorschlagTag, setzeVorschlagTag] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  /* Auftrag CRM-103: Die Ablehnung ist endgültig und fragt deshalb nach. */
  const [rueckfrage, setzeRueckfrage] = useState(false);

  const offen = alternative.antwort === null;

  useEffect(() => {
    if (!offen) return;
    void (async () => {
      try {
        /*
         * Die Termine DES WUNSCHTAGES — nach Produkt gefiltert, wenn die
         * Anfrage eines nennt. Ohne Filter stünden an einem Betriebstag
         * zwanzig Termine zur Wahl, von denen neunzehn nichts damit zu tun
         * haben.
         */
        const gefunden = await kalender(
          alternative.wunschtag,
          alternative.wunschtag,
          anfrage.artikel === null ? {} : { artikel: anfrage.artikel },
        );
        setzeTermine(gefunden);
      } catch (e) {
        if (!(e instanceof ApiFehler)) throw e;
        setzeFehler(e);
      }
    })();
  }, [offen, alternative.wunschtag, anfrage.artikel]);

  async function fuehreAus(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await was();
      await beiAntwort();
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    } finally {
      setzeLaeuft(false);
    }
  }

  /**
   * Nach der bestätigten Ablehnung neu laden. Die Ablehnung selbst ist dann
   * geschehen — ein Fehler beim Laden gehört in die Zeile, nicht ins Pop-up,
   * sonst lädt ein zweiter Klick zu einer schon beantworteten Alternative ein.
   */
  async function nachAblehnung(): Promise<void> {
    try {
      await beiAntwort();
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    }
  }

  if (!offen) {
    return (
      <li className="optionen__alternative">
        <strong>{alsTag(alternative.wunschtag)}</strong>{' '}
        <span
          className={
            alternative.antwort === 'zugesagt' ? 'marke marke--gut' : 'marke marke--warn'
          }
        >
          {alternative.antwort === 'zugesagt'
            ? 'zugesagt'
            : alternative.antwort === 'abgelehnt'
              ? 'abgelehnt'
              : 'Vorschlag'}
        </span>
        <p className="hilfe">
          {alternative.beantwortername ?? 'unbekannt'} am{' '}
          {alternative.beantwortetAm === null ? '—' : alsZeitpunkt(alternative.beantwortetAm)}
          {alternative.fristBis !== null && (
            <>
              {' '}
              — gehalten bis {alsTag(alternative.fristBis)}
              {alternative.verlaengertAm !== null && ' (verlängert)'}
            </>
          )}
          {alternative.grund !== null && <> — {alternative.grund}</>}
          {alternative.vorschlagTag !== null && (
            <> — Vorschlag: {alsTag(alternative.vorschlagTag)}</>
          )}
        </p>
        {alternative.antwort === 'zugesagt' && (
          <Verlaengerung alternative={alternative} beiAntwort={beiAntwort} />
        )}
      </li>
    );
  }

  return (
    <li className="optionen__alternative">
      <strong>{alsTag(alternative.wunschtag)}</strong>
      {fehler !== null && <Meldung fehler={fehler} />}

      <div className="optionen__antwort">
        <label className="liste__filter">
          Termin{' '}
          <select
            aria-label={`Termin am ${alsTag(alternative.wunschtag)}`}
            value={termin}
            onChange={(e) => {
              setzeTermin(e.target.value);
            }}
          >
            <option value="">— wählen —</option>
            {termine.map((t) => (
              <option key={t.id} value={t.id}>
                {alsZeitpunkt(t.beginn)} · {t.belegt}/{t.hoechstteilnehmer} belegt
              </option>
            ))}
          </select>
        </label>{' '}
        <label className="liste__filter">
          gehalten bis{' '}
          <input
            type="date"
            aria-label={`Optionsfrist für den ${alsTag(alternative.wunschtag)}`}
            value={frist}
            min={heutePlus(1)}
            onChange={(e) => {
              setzeFrist(e.target.value);
            }}
          />
        </label>{' '}
        <button
          type="button"
          className="knopf knopf--haupt knopf--klein"
          disabled={laeuft || termin === ''}
          onClick={() => {
            void fuehreAus(async () => {
              await zusagen(alternative.id, { termin, fristBis: frist === '' ? null : frist });
            });
          }}
        >
          Zusagen
        </button>
        {termine.length === 0 && (
          <p className="hilfe">
            An diesem Tag steht kein Termin bereit. Legen Sie ihn in der Terminverwaltung an
            oder schlagen Sie einen anderen Tag vor.
          </p>
        )}
      </div>

      <div className="optionen__antwort">
        <label className="liste__filter">
          Grund{' '}
          <input
            type="text"
            aria-label={`Grund für den ${alsTag(alternative.wunschtag)}`}
            value={grund}
            maxLength={500}
            onChange={(e) => {
              setzeGrund(e.target.value);
            }}
          />
        </label>{' '}
        <label className="liste__filter">
          Vorschlag{' '}
          <input
            type="date"
            aria-label={`Terminvorschlag statt ${alsTag(alternative.wunschtag)}`}
            value={vorschlagTag}
            onChange={(e) => {
              setzeVorschlagTag(e.target.value);
            }}
          />
        </label>{' '}
        <button
          type="button"
          className="knopf knopf--klein"
          disabled={laeuft || grund.trim() === ''}
          onClick={() => setzeRueckfrage(true)}
        >
          Ablehnen …
        </button>{' '}
        <button
          type="button"
          className="knopf knopf--klein"
          disabled={laeuft || vorschlagTag === ''}
          onClick={() => {
            void fuehreAus(() =>
              vorschlagen(alternative.id, vorschlagTag, grund.trim() === '' ? null : grund),
            );
          }}
        >
          Anderen Termin vorschlagen
        </button>
      </div>
      {/* ANG-42: Eine Ablehnung trägt einen Grund; ein Vorschlag braucht einen Tag. */}
      <p className="hilfe">
        Eine Ablehnung braucht einen Grund — er steht später im Vorgang und geht an den
        Vertrieb. Ein Vorschlag braucht einen Tag.
      </p>

      {/* Die Rückfrage steht im Fenster der Anfrage; Esc schließt nur sie (Dialog). */}
      {rueckfrage && (
        <Rueckfrage
          frage="Wunschtermin ablehnen?"
          unterzeile={`${anfrage.angebotsnummer} · ${anfrage.leistung}`}
          tat="Ablehnen"
          beiSchliessen={() => setzeRueckfrage(false)}
          beiBestaetigen={async () => {
            await ablehnen(alternative.id, grund, vorschlagTag === '' ? null : vorschlagTag);
            setzeRueckfrage(false);
            await nachAblehnung();
          }}
        >
          <p>
            Der Wunschtermin am <strong>{alsTag(alternative.wunschtag)}</strong>
            {alternative.wunschzeit !== null && `, ${alternative.wunschzeit.slice(0, 5)} Uhr`}{' '}
            für {anfrage.teilnehmerzahl} Teilnehmer wird abgelehnt. Grund: „{grund.trim()}“
            {vorschlagTag !== '' && ` — Vorschlag: ${alsTag(vorschlagTag)}`}.
          </p>
          {/* ANG-32, ANG-42 */}
          <p>Die Antwort lässt sich danach nicht mehr ändern; der Grund geht an den Vertrieb.</p>
        </Rueckfrage>
      )}
    </li>
  );
}

/** ANG-35: Verlängern geht — mit Grund, und nur nach vorn. */
function Verlaengerung({
  alternative,
  beiAntwort,
}: {
  readonly alternative: Optionsalternative;
  readonly beiAntwort: () => Promise<void>;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const [bis, setzeBis] = useState(
    alternative.fristBis === null ? '' : alsFeldtag(alternative.fristBis),
  );
  const [grund, setzeGrund] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  if (!offen) {
    return (
      <button
        type="button"
        className="knopf knopf--verweis"
        onClick={() => {
          setzeOffen(true);
        }}
      >
        Frist verlängern
      </button>
    );
  }

  return (
    <div className="optionen__antwort">
      {fehler !== null && <Meldung fehler={fehler} />}
      <label className="liste__filter">
        neu bis{' '}
        <input
          type="date"
          aria-label="Neue Optionsfrist"
          value={bis}
          onChange={(e) => {
            setzeBis(e.target.value);
          }}
        />
      </label>{' '}
      <label className="liste__filter">
        Grund{' '}
        <input
          type="text"
          aria-label="Grund der Verlängerung"
          value={grund}
          maxLength={500}
          onChange={(e) => {
            setzeGrund(e.target.value);
          }}
        />
      </label>{' '}
      <button
        type="button"
        className="knopf knopf--klein"
        disabled={bis === '' || grund.trim() === ''}
        onClick={() => {
          void (async () => {
            setzeFehler(null);
            try {
              await verlaengern(alternative.id, bis, grund);
              setzeOffen(false);
              await beiAntwort();
            } catch (e) {
              if (!(e instanceof ApiFehler)) throw e;
              setzeFehler(e);
            }
          })();
        }}
      >
        Verlängern
      </button>
    </div>
  );
}

/**
 * Der Tag, den der Klick auf den Kunden öffnet (Auftrag CRM-69): der erste noch
 * offene Wunsch — der beantwortete ist entschieden, den will niemand mehr
 * ansehen. Sind alle beantwortet (die Zeile wartet dann auf den Server), der
 * erste.
 */
function ersterOffenerWunsch(z: Optionsarbeitszeile): Optionswunschtag | undefined {
  return z.wuensche.find((w) => w.offen) ?? z.wuensche[0];
}

export function Optionsanfragen({
  beiAngebot,
  beiTagesplan,
}: {
  readonly beiAngebot: (angebot: string) => void;
  /**
   * Der Sprung in die Trainingsplanung (Auftrag CRM-69). Fehlt er — kein
   * `tagesplan/sehen` (BEN-02) —, führt der Kunde wie bisher ins Angebot, und
   * die Wunschtage bleiben Text.
   */
  readonly beiTagesplan?: (tag: string, standort: string | undefined) => void;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Optionsarbeitszeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [offen, setzeOffen] = useState<Optionsanfrage | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeZeilen(await ladeArbeitsliste());
      setzeFehler(null);
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  /**
   * Nach jeder Antwort neu laden — und das Fenster mit.
   *
   * NICHT ÖRTLICH FORTGESCHRIEBEN: An dieser Liste arbeiten mehrere. Wer seine
   * eigene Eingabe fortschreibt, zeigt danach einen Stand, den es auf dem
   * Server nicht gibt — und beantwortet womöglich eine Alternative zweimal.
   */
  const nachAntwort = useCallback(async () => {
    if (offen !== null) {
      const neu = await ladeEine(offen.id);
      setzeOffen(neu);
    }
    await holen();
  }, [offen, holen]);

  const eskalierte = zeilen.filter((z) => z.eskaliert).length;

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(zeilen.length, 'offene Anfrage', 'offene Anfragen'));

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        hinweise={
          /*
            DER STREIFEN OBEN SAGT, OB ETWAS BRENNT (ANG-37).

            Die Zahl der eskalierten Anfragen ist die einzige, die den Tag ändert:
            Sie steht für Kunden, die auf eine Antwort warten, die zugesagt war.
          */
          laedt ? undefined : (
            <div
              className={
                eskalierte > 0
                  ? 'streifen streifen--warn'
                  : zeilen.length === 0
                    ? 'streifen streifen--gut'
                    : 'streifen'
              }
            >
              {zeilen.length === 0 ? (
                <>Keine offene Terminoptionsanfrage.</>
              ) : eskalierte > 0 ? (
                <>
                  <strong>
                    {eskalierte} von {zeilen.length} Anfragen überfällig
                  </strong>{' '}
                  — die Antwortfrist ist verstrichen. Der Vertrieb wartet, und der Kunde wartet
                  auf den Vertrieb.
                </>
              ) : (
                <>
                  {zeilen.length} offene {zeilen.length === 1 ? 'Anfrage' : 'Anfragen'} — nach
                  Dringlichkeit sortiert.
                </>
              )}
            </div>
          )
        }
      >
        {/* ANG-38 */}
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{zeilen.length} offene Terminoptionsanfragen</caption>
            <thead>
              <tr>
                <th scope="col">Kunde</th>
                <th scope="col">Leistung</th>
                <th scope="col" className="zahl">
                  Plätze
                </th>
                <th scope="col">Wunschtermine</th>
                <th scope="col">Angefragt von</th>
                <th scope="col">Eingang</th>
                <th scope="col">Restfrist</th>
                <th scope="col">
                  <span className="nur-vorlesen">Antwort</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => {
                const erster = ersterOffenerWunsch(z);
                return (
                  <tr key={z.anfrage} className={z.eskaliert ? 'optionen__zeile--spaet' : undefined}>
                    <td>
                      {/*
                        Auftrag CRM-69 (Ulf, 06.10.2026): „statt des Angebots soll
                        sich der tagesplan öffnen." Der Kunde führt in den ersten
                        offenen Wunschtag — wer antwortet, will sehen, wie der Tag
                        belegt ist. Das Angebot bleibt über die Nummer daneben.
                      */}
                      {beiTagesplan !== undefined && erster !== undefined ? (
                        <Tagesverweis
                          tag={erster.tag}
                          standort={erster.standort ?? undefined}
                          beiWahl={beiTagesplan}
                          className="liste__oeffnen"
                        >
                          {z.kundenname}
                        </Tagesverweis>
                      ) : (
                        <button
                          type="button"
                          className="liste__oeffnen"
                          onClick={() => {
                            beiAngebot(z.angebot);
                          }}
                        >
                          {z.kundenname}
                        </button>
                      )}
                      <span className="kanalliste__name">
                        {' · '}
                        <Maskenverweis className="liste__nummer"
                          maske={{ art: 'angebotsakte', id: z.angebot }}
                          title="Angebot öffnen"
                          beiKlick={() => {
                            beiAngebot(z.angebot);
                          }}
                        >
                          {z.angebotsnummer}
                          <span className="nur-vorlesen"> — Angebot öffnen</span>
                        </Maskenverweis>
                      </span>
                    </td>
                    <td>{z.leistung}</td>
                    <td className="zahl">{z.teilnehmerzahl}</td>
                    <td>
                      {/*
                        MIT UHRZEIT (15.09.2026). Seit AeA-26 beginnt ein Training
                        im Raster von 45 Minuten ab 08:00; ob der Kunde 08:00 oder
                        09:30 wünscht, entscheidet über Trainer, Fläche und
                        Anschlusstermin. Die Angebotsakte zeigte beides, die Liste,
                        auf der entschieden wird, nur den Tag — wer antworten
                        wollte, musste erst ins Angebot springen.
                      */}
                      {/*
                        Auftrag CRM-69: Jeder Wunschtag ist ein eigener Verweis auf
                        seinen Tag in der Trainingsplanung — derselbe Einstieg wie
                        aus den Terminen (CRM-67), kein zweiter.
                      */}
                      {z.wunschtage.map((w, i) => {
                        const wunsch = z.wuensche[i];
                        return (
                          <span key={w}>
                            {i > 0 && ' · '}
                            {wunsch === undefined ? (
                              alsWunsch(w)
                            ) : (
                              <Tagesverweis
                                tag={wunsch.tag}
                                standort={wunsch.standort ?? undefined}
                                beiWahl={beiTagesplan}
                              >
                                {alsWunsch(w)}
                              </Tagesverweis>
                            )}
                          </span>
                        );
                      })}
                      {z.offene < z.alternativen && (
                        <span className="hilfe">
                          {' '}
                          ({z.alternativen - z.offene} von {z.alternativen} beantwortet)
                        </span>
                      )}
                    </td>
                    <td>{z.anfragender}</td>
                    <td className="liste__einzeilig">{alsZeitpunkt(z.eingegangenAm)}</td>
                    <td>
                      {z.eskaliert ? (
                        <span className="marke marke--krit">{restfrist(z.restminuten)}</span>
                      ) : (
                        restfrist(z.restminuten)
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        onClick={() => {
                          void (async () => {
                            try {
                              setzeOffen(await ladeEine(z.anfrage));
                            } catch (e) {
                              if (!(e instanceof ApiFehler)) throw e;
                              setzeFehler(e);
                            }
                          })();
                        }}
                      >
                        Beantworten
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {laedt && zeilen.length === 0 && (
          <p className="liste__leer" role="status">
            Wird geladen …
          </p>
        )}
        {!laedt && zeilen.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              Sobald der Vertrieb an einem Angebot Termine anfragt, stehen sie hier — und sie
              bleiben stehen, bis jede Alternative beantwortet ist.
            </p>
          </div>
        )}
      </Liste>

      {offen !== null && (
        <Dialog
          titel={`Terminoption · ${offen.angebotsnummer}`}
          unterzeile={`${offen.leistung} für ${String(offen.teilnehmerzahl)} Teilnehmer`}
          breite="breit"
          beiSchliessen={() => {
            setzeOffen(null);
          }}
        >
          <p className="hilfe">
            Angefragt von {offen.anfragendername} am {alsZeitpunkt(offen.eingegangenAm)}
            {offen.antwortfristBis === null
              ? ' — ohne Antwortfrist (der Vorgabewert ist nicht hinterlegt).'
              : ` — zu beantworten bis ${alsZeitpunkt(offen.antwortfristBis)}.`}
            {offen.gewuenschteFristBis !== null && (
              <> Gewünschte Optionsfrist: {alsTag(offen.gewuenschteFristBis)}.</>
            )}
          </p>
          {offen.zusatzleistungen !== null && (
            <p className="hilfe">Zusatzleistungen: {offen.zusatzleistungen}</p>
          )}
          {offen.bemerkung !== null && <p className="hilfe">{offen.bemerkung}</p>}

          <ul className="optionen__alternativen">
            {offen.alternativen.map((a) => (
              <Alternativzeile
                key={a.id}
                anfrage={offen}
                alternative={a}
                beiAntwort={nachAntwort}
              />
            ))}
          </ul>
        </Dialog>
      )}
    </>
  );
}
